import { createEvent, createStore } from 'effector';
import {
    type BaseOpenResponsiveModalPayload,
    type ResponsiveModalEntry,
    type ResponsiveModalsState,
    type UpdateResponsiveModalPayload,
} from './types';

const initialResponsiveModalsState: ResponsiveModalsState = {
    entries: [],
    nextOpenId: 1,
};

export const responsiveModalOpened = createEvent<BaseOpenResponsiveModalPayload>();
export const responsiveModalClosed = createEvent<string | undefined>();
export const responsiveModalUpdated = createEvent<UpdateResponsiveModalPayload>();
export const responsiveModalDiscarded = createEvent<number>();
export const responsiveModalsCleared = createEvent();

export const $responsiveModals = createStore<ResponsiveModalsState>(initialResponsiveModalsState)
    .on(responsiveModalOpened, (state, payload) => {
        const modalId = payload.modalId ?? payload.modal;
        const entry: ResponsiveModalEntry = {
            ...payload,
            innerProps: payload.innerProps ?? {},
            modalId,
            openId: state.nextOpenId,
        };

        return {
            entries: [...state.entries.filter((currentEntry) => currentEntry.modalId !== modalId), entry],
            nextOpenId: state.nextOpenId + 1,
        };
    })
    .on(responsiveModalClosed, (state, modalId) => {
        if (modalId === undefined) {
            if (state.entries.length === 0) {
                return state;
            }

            return {
                ...state,
                entries: state.entries.slice(0, -1),
            };
        }

        const entryIndex = state.entries.findIndex((entry) => entry.modalId === modalId);

        if (entryIndex === -1) {
            return state;
        }

        return {
            ...state,
            entries: state.entries.slice(0, entryIndex),
        };
    })
    .on(responsiveModalUpdated, (state, payload) => {
        const currentEntry = state.entries.find((entry) => entry.modalId === payload.modalId);

        if (!currentEntry) {
            return state;
        }

        const updatedEntry: ResponsiveModalEntry = {
            ...currentEntry,
            ...(payload.drawerProps !== undefined && {
                drawerProps: { ...currentEntry.drawerProps, ...payload.drawerProps },
            }),
            ...(payload.innerProps !== undefined && {
                innerProps: { ...currentEntry.innerProps, ...payload.innerProps },
            }),
            ...(payload.modalProps !== undefined && {
                modalProps: { ...currentEntry.modalProps, ...payload.modalProps },
            }),
            ...('title' in payload && { title: payload.title }),
        };

        return {
            ...state,
            entries: state.entries.map((entry) => (entry.modalId === payload.modalId ? updatedEntry : entry)),
        };
    })
    .on(responsiveModalDiscarded, (state, openId) => {
        const entries = state.entries.filter((entry) => entry.openId !== openId);

        if (entries.length === state.entries.length) {
            return state;
        }

        return {
            ...state,
            entries,
        };
    })
    .reset(responsiveModalsCleared);
